"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Flame, Skull, Newspaper, ArrowUpRight } from "lucide-react";
import CdnImage from "@/components/CdnImage";
import { useT } from "@/hooks/useT";
import { decodeHTMLEntities } from "@/lib/sanitize";
import { timeAgo } from "../../helpers/format";
import { CARD_BASE } from "../../helpers/styles";
import type { ActivityEvent } from "../../types";

// Blog Article Card
//
// Inlines the blog_article entry from EVENT_STYLES (iconBg + iconColor)
// since it's the only branch that consumed those fields. JSX
// byte-identical to the inline version pulled out of EventCard.
export default function BlogArticleRow({
  event,
  onVote,
}: {
  event: ActivityEvent;
  onVote: (eventId: string, voteType: number, sourceTable: string) => void;
}) {
  const t = useT();
  const router = useRouter();

  const slug = event.metadata?.slug;
  const title = event.metadata?.title || event.headline?.replace(/^📰\s*New article:\s*/i, '') || 'New article';
  const excerpt = event.metadata?.excerpt || event.description || '';
  const coverUrl = event.metadata?.cover_image_url || event.market_image;
  const traderUsername = event.metadata?.trader_username;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={() => slug && router.push(`/blog/${slug}`)}
      className={`${CARD_BASE} py-3.5`}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-[5px] bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
          <Newspaper className="w-4 h-4 text-white" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[10px] font-bold tracking-wide text-sky-500 bg-sky-500/10 px-1.5 py-0.5 rounded flex-shrink-0">ARTICLE</span>
              <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">{timeAgo(event.event_at, t)}</span>
            </div>
          </div>

          {/* Article preview */}
          <div className="mt-2 flex gap-3">
            {coverUrl && (
              <div className="w-20 h-14 rounded-[5px] overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-[#18181D]">
                <CdnImage src={coverUrl} alt="" width={80} height={56} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-gray-900 dark:text-white line-clamp-2 leading-snug">{decodeHTMLEntities(title)}</p>
              {excerpt && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">{decodeHTMLEntities(excerpt)}</p>
              )}
              {traderUsername && (
                <p className="text-[10px] text-gray-400 mt-1">About @{traderUsername}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1.5">
              <button
                onClick={(e) => { e.stopPropagation(); onVote(event.id, 1, 'activity_event'); }}
                className={`flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs transition-colors ${
                  event.user_vote === 1 ? 'text-emerald-500 bg-emerald-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-emerald-500'
                }`}
              >
                <Flame className="w-3.5 h-3.5" />
                <span>{event.reactions?.fire || event.likes || 0}</span>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onVote(event.id, -1, 'activity_event'); }}
                className={`flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs transition-colors ${
                  event.user_vote === -1 ? 'text-red-500 bg-red-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-red-500'
                }`}
              >
                <Skull className="w-3.5 h-3.5" />
                <span>{event.reactions?.skull || event.dislikes || 0}</span>
              </button>
            </div>
            {slug && (
              <Link
                href={`/blog/${slug}`}
                onClick={(e) => e.stopPropagation()}
                className="text-[11px] font-medium text-sky-500 hover:text-sky-400 transition-colors flex items-center gap-1"
              >
                Read <ArrowUpRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
